import React, { useState } from 'react';
import { db } from '../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { UserProfile } from '../types';
import { validatePassword } from '../lib/utils';
import { toast } from 'sonner';
import { ShieldAlert, LogOut, ChevronRight } from 'lucide-react';

interface ForcePasswordResetProps {
  profile: UserProfile;
  onLogout: () => void;
}

export default function ForcePasswordReset({ profile, onLogout }: ForcePasswordResetProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error('ALL FIELDS ARE REQUIRED');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('PASSCODES DO NOT MATCH');
      return;
    }
    if (newPassword === 'Z@v@l@') {
      toast.error('YOU CANNOT REUSE THE DEFAULT PASSCODE');
      return;
    }

    const errorMsg = validatePassword(newPassword);
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }

    setIsLoading(true);
    try {
      const userRef = doc(db, 'users', profile.uid);
      await updateDoc(userRef, {
        password: newPassword,
        passwordLastChanged: new Date().toISOString()
      });
      toast.success('Passcode updated successfully. Access authorization complete.');
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to update passcode: ' + (err.message || 'Firestore write error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--background)] text-[var(--foreground)] font-lcars flex flex-col justify-between p-4 lg:p-8">
      {/* Header */}
      <div className="flex gap-2 items-end shrink-0">
        <div className="h-16 w-16 lg:w-24 bg-lcars-red rounded-tl-full flex items-center justify-center">
          <ShieldAlert className="w-8 h-8 text-black" />
        </div>
        <div className="h-8 flex-1 bg-lcars-red flex items-center px-4 lg:px-6">
          <span className="text-black font-bold text-base lg:text-xl tracking-widest uppercase truncate">
            SECURITY ALERT: ENCRYPTION CREDENTIALS DETECTED AT DEFAULT SETTING
          </span>
        </div>
        <div className="h-16 w-12 bg-lcars-red rounded-tr-full"></div>
      </div>

      {/* Main content */}
      <div className="max-w-2xl mx-auto w-full py-8 space-y-6 flex-1 flex flex-col justify-center">
        <div className="bg-[var(--background)]/60 border-2 border-lcars-red/30 p-6 lg:p-10 rounded-r-[40px] space-y-6">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-lcars-red uppercase tracking-wider">
              Regenerate Security Passcode
            </h2>
            <p className="text-sm text-[var(--foreground)]/80 leading-relaxed uppercase">
              Officer {profile.firstName} {profile.lastName}, the system detected that you are authenticated using the default starship passcode. Starfleet security protocol SEC-REG-801 requires that default credentials be replaced with a secure password before core system modules can be accessed.
            </p>
            <div className="p-4 bg-lcars-gold/10 border border-lcars-gold/20 rounded-md text-xs text-lcars-gold space-y-1">
              <span className="font-bold block uppercase mb-1">Passcode Requirements:</span>
              <ul className="list-disc pl-4 space-y-0.5 uppercase">
                <li>Minimum of 8 characters in length</li>
                <li>At least one uppercase character (A-Z)</li>
                <li>At least one lowercase character (a-z)</li>
                <li>At least one numeric digit (0-9)</li>
                <li>At least one special character (e.g. @, #, $, %, etc.)</li>
              </ul>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-1">
                <label htmlFor="reset-new-pass" className="text-[0.6rem] text-[var(--foreground)]/50 font-bold uppercase tracking-widest block pl-2">
                  New Security Passcode
                </label>
                <input 
                  id="reset-new-pass"
                  name="resetNewPass"
                  type="password" 
                  placeholder="ENTER NEW PASSCODE" 
                  className="w-full bg-[var(--background)] border-2 border-lcars-red/30 rounded-full py-3 px-6 text-[var(--foreground)] focus:border-lcars-red outline-none transition-all uppercase text-xs tracking-widest"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="reset-confirm-pass" className="text-[0.6rem] text-[var(--foreground)]/50 font-bold uppercase tracking-widest block pl-2">
                  Confirm Security Passcode
                </label>
                <input 
                  id="reset-confirm-pass"
                  name="resetConfirmPass"
                  type="password" 
                  placeholder="CONFIRM NEW PASSCODE" 
                  className="w-full bg-[var(--background)] border-2 border-lcars-red/30 rounded-full py-3 px-6 text-[var(--foreground)] focus:border-lcars-red outline-none transition-all uppercase text-xs tracking-widest"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit"
                disabled={isLoading}
                className="flex-1 lcars-pill bg-lcars-gold text-black font-bold py-3 text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-lcars-gold/80 transition-colors cursor-pointer"
              >
                {isLoading ? 'UPDATING CORE...' : 'UPDATE CREDENTIALS'} <ChevronRight className="w-4 h-4" />
              </button>
              <button 
                type="button"
                onClick={onLogout}
                disabled={isLoading}
                className="lcars-pill bg-lcars-red text-[var(--foreground)] font-bold py-3 px-8 text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-lcars-red/80 transition-colors cursor-pointer"
              >
                <LogOut className="w-4 h-4" /> LOGOUT
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-2 items-start shrink-0 mt-4">
        <div className="h-12 w-12 bg-lcars-red rounded-bl-full"></div>
        <div className="h-4 flex-1 bg-lcars-red"></div>
        <div className="h-12 w-24 bg-lcars-red rounded-br-full"></div>
      </div>
    </div>
  );
}
